import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users, Search, Filter, Download,
  Target, BookOpen,
  AlertCircle, XCircle, Trophy,
  Loader2, FileDown, RefreshCw, Award,
} from 'lucide-react';
import { courseAssignmentAnalyticsService } from '@/services/courseAssignmentAnalytics.service';
import { toast } from '@/components/ui/Toast';
import { exportService, type ExportRoleAnalyticsParams } from '@/services/export.service';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import Pagination from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useForm } from 'react-hook-form';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { ROLE_PROGRESS_PERMISSION } from '@/constants/constant-permissions';

interface FilterOptions {
  searchQuery: string;
  engagementLevel: 'all' | 'high' | 'medium' | 'low';
  completionRange: 'all' | '0to25' | '26to50' | '51to75' | '76to100';
  page: number;
  limit: number;
}

interface SelectFormValues {
  engagementLevel: string;
  completionRange: string;
}

export default function RoleProgress() {
  usePermissionRouteAccess([ROLE_PROGRESS_PERMISSION.ADMIN_ROLE_PROGRESS_READ, ROLE_PROGRESS_PERMISSION.ADMIN_ROLE_PROGRESS_WRITE]);
  const canWrite = useHasPermission([ROLE_PROGRESS_PERMISSION.ADMIN_ROLE_PROGRESS_WRITE]);

  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, DEFAULT_DEBOUNCE_DELAY);

  const [filters, setFilters] = useState<FilterOptions>({
    searchQuery: '',
    engagementLevel: 'all',
    completionRange: 'all',
    page: 1,
    limit: 10
  });

  const [exportLoading, setExportLoading] = useState<{
    excel: boolean;
    csv: boolean;
  }>({ excel: false, csv: false });

  // Form setup for Select components
  const { control, watch, setValue } = useForm<SelectFormValues>({
    defaultValues: {
      engagementLevel: 'all',
      completionRange: 'all'
    }
  });

  // Watch form changes and sync with state
  useEffect(() => {
    const subscription = watch((value) => {
      if (value.engagementLevel !== undefined) {
        setFilters(prev => ({ ...prev, engagementLevel: value.engagementLevel as any, page: 1 }));
      }
      if (value.completionRange !== undefined) {
        setFilters(prev => ({ ...prev, completionRange: value.completionRange as any, page: 1 }));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Update filters when debounced search changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      searchQuery: debouncedSearch,
      page: 1
    }));
  }, [debouncedSearch]);

  // Fetch role analytics data
  const { data: analyticsData, isLoading, error, refetch } = useQuery({
    queryKey: ['role-progress', filters.page, filters.limit, filters.searchQuery, filters.engagementLevel, filters.completionRange],
    queryFn: () => courseAssignmentAnalyticsService.getRoleAnalytics(filters.page, filters.limit,  filters.searchQuery, filters.engagementLevel, filters.completionRange),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Handle export functionality
  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setExportLoading(prev => ({ ...prev, [format]: true }));

      // Build export filters from current UI filters (no pagination)
      const exportFilters: ExportRoleAnalyticsParams = {};

      // Search filter
      if (filters.searchQuery) {
        exportFilters.search = filters.searchQuery;
      }

      // Engagement level filter
      if (filters.engagementLevel !== 'all') {
        exportFilters.engagementLevel = filters.engagementLevel;
      }

      // Completion range filter
      if (filters.completionRange !== 'all') {
        exportFilters.completionRange = filters.completionRange;
      }

      // Call export service
      const result = await exportService.exportRoleAnalytics(format, exportFilters);

      if (!result.success) {
        toast.error(result.message || 'No data available to export');
        return;
      }

      toast.success(`${result.count} role records exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error(`Failed to export data as ${format.toUpperCase()}`);
      console.error('Export error:', error);
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  // Select options
  const engagementOptions = [
    { label: 'All Engagement Levels', value: 'all' },
    { label: 'High Engagement', value: 'high' },
    { label: 'Medium Engagement', value: 'medium' },
    { label: 'Low Engagement', value: 'low' }
  ];

  const completionRangeOptions = [
    { label: 'All Completion Rates', value: 'all' },
    { label: '0-25% (Needs Attention)', value: '0to25' },
    { label: '26-50% (Below Average)', value: '26to50' },
    { label: '51-75% (Average)', value: '51to75' },
    { label: '76-100% (Excellent)', value: '76to100' }
  ];

  // Filter roles based on search and filters
  const filteredRoles = analyticsData ? analyticsData.roles : []

  const getEngagementColor = (engagement: string) => {
    switch (engagement) {
      case 'high':
        return 'bg-green-100 text-green-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Role Learning Progress</h1>
            <p className="text-indigo-100">
              Track role-wide learning metrics and identify high-performing teams
            </p>
          </div>
          <div className="flex gap-3">
            <div className="flex gap-2">
              <button
                onClick={() => canWrite && handleExport('excel')}
                disabled={!canWrite || exportLoading.excel}
                className={`flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${canWrite ? 'hover:bg-indigo-50' : ''}`}
                title={!canWrite ? 'You do not have permission to export' : 'Export as Excel'}
              >
                {exportLoading.excel ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <FileDown className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">Excel</span>
              </button>
              <button
                onClick={() => canWrite && handleExport('csv')}
                disabled={!canWrite || exportLoading.csv}
                className={`flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${canWrite ? 'hover:bg-white/30' : ''}`}
                title={!canWrite ? 'You do not have permission to export' : 'Export as CSV'}
              >
                {exportLoading.csv ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">CSV</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {analyticsData?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-bold text-gray-900">{analyticsData.summary.totalRoles}</span>
            </div>
            <p className="text-sm text-gray-600">Total Roles</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-8 h-8 text-green-600" />
              <span className="text-2xl font-bold text-gray-900">{analyticsData.summary.avgCompletionRate}%</span>
            </div>
            <p className="text-sm text-gray-600">Average Completion Rate</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Trophy className="w-8 h-8 text-yellow-600" />
              <span className="text-lg font-bold text-gray-900">{analyticsData.summary.topPerformingRole}</span>
            </div>
            <p className="text-sm text-gray-600">Top Performing Role</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5 text-indigo-600" />
            Filter & Search Roles
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSearchInput('');
                setValue('engagementLevel', 'all');
                setValue('completionRange', 'all');
                setFilters({
                  searchQuery: '',
                  engagementLevel: 'all',
                  completionRange: 'all',
                  page: 1,
                  limit: 10
                });
              }}
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => refetch()}
              className="flex items-center gap-1 px-3 py-1 text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Search Input - Full Width */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Roles
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by role name"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-500 transition-colors min-h-[42px]"
                style={{ fontSize: '0.875rem' }}
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Engagement Level Filter */}
            <div>
              <Select
                name="engagementLevel"
                control={control}
                label="Engagement Level"
                options={engagementOptions}
                isSearchable={false}
                placeholder="Select engagement level"
              />
              {filters.engagementLevel !== 'all' && (
                <div className="mt-2 text-xs text-indigo-600">
                  Filtered: {engagementOptions.find(e => e.value === filters.engagementLevel)?.label}
                </div>
              )}
            </div>

            {/* Completion Range Filter */}
            <div>
              <Select
                name="completionRange"
                control={control}
                label="Completion Rate Range"
                options={completionRangeOptions}
                isSearchable={false}
                placeholder="Select completion range"
              />
              {filters.completionRange !== 'all' && (
                <div className="mt-2 text-xs text-indigo-600">
                  Filtered: {completionRangeOptions.find(c => c.value === filters.completionRange)?.label}
                </div>
              )}
            </div>
          </div>

          {/* Active Filters Summary */}
          {(searchInput || filters.engagementLevel !== 'all' || filters.completionRange !== 'all') && (
            <div className="pt-3 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm font-medium text-gray-700">Active filters:</span>
                {searchInput && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                    Search: "{searchInput}"
                    <button
                      onClick={() => setSearchInput('')}
                      className="hover:text-indigo-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.engagementLevel !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    Engagement: {filters.engagementLevel.charAt(0).toUpperCase() + filters.engagementLevel.slice(1)}
                    <button
                      onClick={() => {
                        setValue('engagementLevel', 'all');
                        setFilters({ ...filters, engagementLevel: 'all', page: 1 });
                      }}
                      className="hover:text-green-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.completionRange !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                    Completion: {filters.completionRange}%
                    <button
                      onClick={() => {
                        setValue('completionRange', 'all');
                        setFilters({ ...filters, completionRange: 'all', page: 1 });
                      }}
                      className="hover:text-yellow-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Role List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="space-y-0 animate-pulse">
            {/* Table Skeleton */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                      <th key={i} className="px-4 py-3">
                        <div className="h-3 bg-gray-200 rounded w-16 mx-auto"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
                    <tr key={row}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                          <div className="space-y-2">
                            <div className="h-4 w-32 bg-gray-200 rounded"></div>
                            <div className="h-3 w-24 bg-gray-200 rounded"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-12 bg-gray-200 rounded mx-auto"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-12 bg-gray-200 rounded mx-auto"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="h-4 w-12 bg-gray-200 rounded mx-auto"></div>
                          <div className="h-2 w-20 bg-gray-200 rounded mx-auto"></div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-16 bg-gray-200 rounded mx-auto"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="h-4 w-12 bg-gray-200 rounded mx-auto"></div>
                          <div className="h-2 w-20 bg-gray-200 rounded mx-auto"></div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-12 bg-gray-200 rounded mx-auto"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-16 bg-gray-200 rounded mx-auto"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-6 w-16 bg-gray-200 rounded-full mx-auto"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-8 bg-gray-200 rounded mx-auto"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Skeleton */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="h-4 w-32 bg-gray-200 rounded"></div>
              <div className="flex gap-2">
                <div className="h-8 w-8 bg-gray-200 rounded"></div>
                <div className="h-8 w-8 bg-gray-200 rounded"></div>
                <div className="h-8 w-8 bg-gray-200 rounded"></div>
              </div>
              <div className="h-8 w-24 bg-gray-200 rounded"></div>
            </div>
          </div>
        ) : error ? (
          <div className="text-center p-12">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">Failed to load role data</p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Retry
            </button>
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="text-center p-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No roles found matching the filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Users</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Courses</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Course Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Completed Percentage</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Certifications</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cert Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Engagement</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRoles.map((role) => (
                    <tr key={role.roleId} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{role.roleName}</p>
                            <p className="text-xs text-gray-500">ID: {role.roleKey}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{role.totalUsers}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <BookOpen className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{role.assignedCourses}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-sm font-medium text-gray-900">{role.avgProgress}%</span>
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-indigo-500 h-2 rounded-full"
                              style={{ width: `${role.avgProgress}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <div className="flex flex-col text-center">
                            <div className="flex gap-1 text-xs">
                              <span className="text-green-600 font-medium">{role.userStats.completed}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-blue-600 font-medium">{role.userStats.inProgress}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-gray-600 font-medium">{role.userStats.notStarted}</span>
                            </div>
                            <span className="text-xs text-gray-500">C/P/N</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-sm font-medium text-gray-900">{role.completionRate}%</span>
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${role.completionRate}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      {/* Certifications Count */}
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Award className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-gray-900">
                            {role.certification_count || 0}
                          </span>
                        </div>
                      </td>
                      {/* Certification Status */}
                      <td className="px-4 py-4">
                        {(role as any).certification_stats ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="flex flex-col text-center">
                              <div className="flex gap-1 text-xs">
                                <span className="text-green-600 font-medium">{(role as any).certification_stats.completed || 0}</span>
                                <span className="text-gray-400">/</span>
                                <span className="text-blue-600 font-medium">{(role as any).certification_stats.in_progress || 0}</span>
                                <span className="text-gray-400">/</span>
                                <span className="text-gray-600 font-medium">{(role as any).certification_stats.not_started || 0}</span>
                              </div>
                              <span className="text-xs text-gray-500">C/P/N</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEngagementColor(role.performanceMetrics.engagementLevel)}`}>
                          {role.performanceMetrics.engagementLevel.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center">
                          <button 
                            onClick={() =>  navigate(`/admin/roles/${role.roleKey}/courses`,
                              { state: { from: '/admin/analytics/role-progress'  } })
                            }
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="View Users"
                          >
                            <Users className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {analyticsData?.pagination && (
              <Pagination
                currentPage={filters.page}
                totalPages={Math.ceil(analyticsData.pagination.total / filters.limit)}
                totalItems={analyticsData.pagination.total}
                itemsPerPage={filters.limit}
                onPageChange={(page) => setFilters({ ...filters, page })}
                onItemsPerPageChange={(limit) => setFilters({ ...filters, limit, page: 1 })}
                showItemsPerPage={true}
              />
            )}
            </>
        )}
      </div>
    </div>
  );
}